import { Helmet } from 'react-helmet-async';
import ContactUsComponent from '../../components/ContactUsComponent'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faCalendarDays,
  faUsers,
  faTicket,
  faClipboardList,
  faChartLine,
  faLayerGroup,
  faEye,
  faChartSimple,
  faCheckCircle,
  faClock,
  faGlobe
} from '@fortawesome/free-solid-svg-icons'

const technologiesIcon = [
  "../images/react.png",
  "../images/azure.svg",
  "../images/azure-sql.png",
  "../images/nodejs.png",
  "../images/csharp.png",
];

const EventManagementsystem = () => {
  return (
    <>
      <Helmet>
        <title>JJ Eve Case Study | Event Management System by Data Repo</title>
        <meta
          name="description"
          content="Discover how Data Repo built JJ Eve, an event management system that helps organizers manage registrations, attendees, schedules, and event operations efficiently."
        />
      </Helmet>

      <section
        style={{
          backgroundImage: `url(../images/bg-casestudies-box.png)`,
          backgroundSize: "100% 80vh"
        }}
        className="relative flex flex-col justify-center w-full h-[65vh] bg-no-repeat bg-fixed pt-[65px] text-center text-white">
        <h1 className="text-3xl md:text-6xl font-bold uppercase">JJ Eve</h1>
        <p className="mt-6 text-lg md:text-2xl max-w-3xl mx-auto">
          Event Management System Developed by Data Repo
        </p>
      </section>

      <section className="bg-gray-100 py-16 px-6 md:px-20">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-10 items-center">
          <div>
            <h2 className="text-3xl font-bold mb-6">Simplifying Event Planning & Management</h2>
            <p className="text-lg text-gray-700 mb-4">
              Event organizers often manage registrations, attendees, and schedules using
              multiple tools or spreadsheets, which can lead to confusion and inefficiencies.
            </p>
            <p className="text-lg text-gray-700">
              JJ Eve was developed by Data Repo to provide a centralized event management
              platform that helps organizers plan, manage, and execute events more smoothly.
            </p>
          </div>
          <img src="../images/jj-eve.png" alt="Event management dashboard" className="rounded-xl shadow-lg" />
        </div>
      </section>

      <section className="bg-white py-16 px-6 md:px-20">
        <div className="max-w-6xl mx-auto text-center mb-10">
          <h2 className="text-3xl font-bold mb-4">Inside the JJ Eve Dashboard</h2>
          <p className="text-gray-600 max-w-3xl mx-auto">
            JJ Eve gives event organizers complete control over registrations,
            attendees, schedules, and event operations.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          <img src="../images/jj-eve-events.png" className="rounded-xl shadow-lg" alt="jj eve" />
          <img src="../images/jj-eve-templates.png" className="rounded-xl shadow-lg" alt="jj eve" />
        </div>
      </section>

      <section className="bg-gradient-to-r from-blue-50 to-white py-8 px-6 md:px-20 border-y">
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6 text-center md:text-left">

          <div className="flex items-center gap-4 justify-center md:justify-start">
            <div className="bg-blue-100 text-blue-700 p-3 rounded-full text-xl">
              <FontAwesomeIcon icon={faGlobe} />
            </div>
            <div>
              <p className="text-sm uppercase tracking-wide text-gray-500">Live Product</p>
              <p className="text-lg font-semibold text-gray-800">
                JJ Eve helps organizers run events with better coordination and control
              </p>
            </div>
          </div>

          <a
            href="https://jjeve.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block bg-blue-700 text-white font-semibold px-6 py-3 rounded-lg shadow-md hover:bg-blue-800 transition"
          >
            Visit Website →
          </a>
        </div>
      </section>

      <section className="bg-white py-20 px-6 md:px-20">
        <div className="max-w-6xl mx-auto text-center">
          <h2 className="text-4xl font-bold mb-4">Project Objectives</h2>
          <p className="text-gray-600 text-lg mb-14 max-w-3xl mx-auto">
            JJ Eve was designed to streamline event planning, simplify attendee management,
            and provide organizers with clear operational visibility.
          </p>

          <div className="grid md:grid-cols-3 gap-10">
            <div className="bg-gray-50 p-8 rounded-2xl shadow-md">
              <FontAwesomeIcon icon={faLayerGroup} className="text-3xl text-blue-700 mb-4" />
              <h3 className="font-semibold text-xl mb-2">Centralized Event Control</h3>
              <p>Manage registrations, schedules, and attendees from one platform.</p>
            </div>

            <div className="bg-gray-50 p-8 rounded-2xl shadow-md">
              <FontAwesomeIcon icon={faEye} className="text-3xl text-blue-700 mb-4" />
              <h3 className="font-semibold text-xl mb-2">Operational Visibility</h3>
              <p>Give organizers real-time insight into attendance and event progress.</p>
            </div>

            <div className="bg-gray-50 p-8 rounded-2xl shadow-md">
              <FontAwesomeIcon icon={faChartSimple} className="text-3xl text-blue-700 mb-4" />
              <h3 className="font-semibold text-xl mb-2">Scalable Event Platform</h3>
              <p>Support small gatherings and large-scale events with one system.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-gray-100 py-16 px-6 md:px-20">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 items-center">
          <img src="../images/event-manual.jpg" alt="Manual event planning" className="rounded-xl shadow-md" />
          <div>
            <h2 className="text-3xl font-bold mb-6">Event Management Challenges</h2>
            <ul className="list-disc pl-5 text-lg text-gray-700 space-y-3">
              <li>Managing attendee registrations manually</li>
              <li>Difficulty coordinating schedules and sessions</li>
              <li>Lack of visibility into ticket sales or attendance</li>
              <li>Using multiple tools for communication and tracking</li>
            </ul>
          </div>
        </div>
      </section>

      <section className="bg-white py-16 px-6 md:px-20">
        <h2 className="text-3xl font-bold text-center mb-12">Core Software Modules</h2>

        <div className="grid md:grid-cols-3 gap-12 text-center">
          <div>
            <FontAwesomeIcon icon={faCalendarDays} className="text-4xl text-blue-700 mb-4" />
            <h3 className="text-xl font-semibold mb-2">Event Scheduling</h3>
            <p>Create and manage event agendas, sessions, and timelines.</p>
          </div>

          <div>
            <FontAwesomeIcon icon={faUsers} className="text-4xl text-blue-700 mb-4" />
            <h3 className="text-xl font-semibold mb-2">Attendee Management</h3>
            <p>Track registrations and manage attendee information efficiently.</p>
          </div>

          <div>
            <FontAwesomeIcon icon={faTicket} className="text-4xl text-blue-700 mb-4" />
            <h3 className="text-xl font-semibold mb-2">Ticketing & Registration</h3>
            <p>Handle event registrations and ticket management in one place.</p>
          </div>

          <div>
            <FontAwesomeIcon icon={faClipboardList} className="text-4xl text-blue-700 mb-4" />
            <h3 className="text-xl font-semibold mb-2">Task & Vendor Management</h3>
            <p>Organize event tasks and coordinate with vendors and staff.</p>
          </div>

          <div>
            <FontAwesomeIcon icon={faChartLine} className="text-4xl text-blue-700 mb-4" />
            <h3 className="text-xl font-semibold mb-2">Event Reports</h3>
            <p>Analyze attendance, registrations, and overall event performance.</p>
          </div>

          <div>
            <FontAwesomeIcon icon={faLayerGroup} className="text-4xl text-blue-700 mb-4" />
            <h3 className="text-xl font-semibold mb-2">Multi-Event Support</h3>
            <p>Manage multiple events simultaneously from one dashboard.</p>
          </div>
        </div>
      </section>

      <section className="bg-gray-100 py-16 px-6 md:px-20">
        <div className="max-w-6xl mx-auto text-center mb-10">
          <h2 className="text-3xl font-bold mb-4">Key Features in Action</h2>
          <p className="text-gray-600 max-w-3xl mx-auto">
            Manage event schedules, attendees, ticketing, and performance insights
            with a single platform.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          <img src="../images/jj-eve-guests.png" className="rounded-xl shadow-lg" alt="jj eve" />
          <img src="../images/jj-eve-programs.png" className="rounded-xl shadow-lg" alt="jj eve" />
          <img src="../images/jj-eve-users-management.png" className="rounded-xl shadow-lg" alt="jj eve" />
          <img src="../images/jj-eve-daily-notes.png" className="rounded-xl shadow-lg" alt="jj eve" />
        </div>
      </section>

      <section className="bg-gradient-to-b from-gray-100 to-white py-20 px-6 md:px-20 text-center">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold mb-4">Business Impact</h2>
          <p className="text-gray-600 text-lg mb-14 max-w-3xl mx-auto">
            Organizers using JJ Eve experienced smoother event coordination, better attendee tracking,
            and improved visibility into event performance.
          </p>

          <div className="grid md:grid-cols-3 gap-10">
            <div className="bg-white p-10 rounded-2xl shadow-md">
              <FontAwesomeIcon icon={faCheckCircle} className="text-3xl text-green-600 mb-4" />
              <h3 className="font-semibold text-lg">Better Coordination</h3>
            </div>
            <div className="bg-white p-10 rounded-2xl shadow-md">
              <FontAwesomeIcon icon={faClock} className="text-3xl text-blue-600 mb-4" />
              <h3 className="font-semibold text-lg">Faster Registrations</h3>
            </div>
            <div className="bg-white p-10 rounded-2xl shadow-md">
              <FontAwesomeIcon icon={faChartLine} className="text-3xl text-yellow-600 mb-4" />
              <h3 className="font-semibold text-lg">Clear Event Insights</h3>
            </div>
          </div>
        </div>
      </section>

      <section className="flex flex-col items-center py-16 px-8">
        <h2 className="text-4xl font-bold mb-8">Technologies Used</h2>
        <div className="flex flex-wrap gap-8 justify-center">
          {technologiesIcon.map((item, index) => (
            <img key={index} src={item} alt="technology" width="90" />
          ))}
        </div>
      </section>

      <ContactUsComponent />
    </>
  )
}

export default EventManagementsystem